import type { RefObject } from 'react'
import { useEffect, useRef } from 'react'

interface UseContainerHeightReportingParams {
  rootRef: RefObject<HTMLElement | null>
  onHeightChange?: (height: number) => void
  freezeGrowthWhenDescendantScrollableSelector?: string
  maxReportedHeight?: number
  minDelta?: number
  heightOffset?: number
}

/**
 * Observes and reports a container's effective rendered height.
 *
 * Decouples DOM measurement/ResizeObserver plumbing from component render logic.
 * Provides a stable, reusable place for layout guards, for example freezing
 * upward growth once an inner editor becomes scrollable.
 */
export function useContainerHeightReporting({
  rootRef,
  onHeightChange,
  freezeGrowthWhenDescendantScrollableSelector,
  maxReportedHeight,
  minDelta = 2,
  heightOffset = 0,
}: UseContainerHeightReportingParams) {
  const frameRef = useRef<number | null>(null)
  const lastReportedHeightRef = useRef<number>(0)

  useEffect(() => {
    if (!onHeightChange || !rootRef.current) return

    lastReportedHeightRef.current = 0

    const reportMeasuredHeight = () => {
      const rootElement = rootRef.current
      if (!rootElement) return

      const rect = rootElement.getBoundingClientRect()
      const styles = window.getComputedStyle(rootElement)
      const marginTop = Number.parseFloat(styles.marginTop || '0') || 0
      const marginBottom = Number.parseFloat(styles.marginBottom || '0') || 0
      const measuredHeight = Math.ceil(rect.height + marginTop + marginBottom)
      const parentVisibleHeight = rootElement.parentElement?.clientHeight
      const nextHeightWithinParent =
        parentVisibleHeight && parentVisibleHeight > 0
          ? Math.min(measuredHeight, parentVisibleHeight)
          : measuredHeight

      let nextReportedHeight = nextHeightWithinParent

      if (freezeGrowthWhenDescendantScrollableSelector) {
        const scrollableElement = rootElement.querySelector(
          freezeGrowthWhenDescendantScrollableSelector
        ) as HTMLElement | null
        const isScrollable =
          !!scrollableElement && scrollableElement.scrollHeight > scrollableElement.clientHeight + 1

        // Once the inner editor starts scrolling, keep outer container height stable.
        // This prevents the composer from "growing forever" while typing long content.
        if (isScrollable && lastReportedHeightRef.current > 0) {
          nextReportedHeight = Math.min(nextHeightWithinParent, lastReportedHeightRef.current)
        }
      }

      if (maxReportedHeight !== undefined) {
        nextReportedHeight = Math.min(nextReportedHeight, maxReportedHeight)
      }

      if (Math.abs(nextReportedHeight - lastReportedHeightRef.current) < minDelta) {
        return
      }

      lastReportedHeightRef.current = nextReportedHeight
      onHeightChange(nextReportedHeight + heightOffset)
    }

    const scheduleMeasurement = () => {
      if (frameRef.current !== null) return

      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null
        reportMeasuredHeight()
      })
    }

    reportMeasuredHeight()

    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => {
      scheduleMeasurement()
    })

    observer.observe(rootRef.current)

    return () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current)
      }
      observer.disconnect()
    }
  }, [
    freezeGrowthWhenDescendantScrollableSelector,
    heightOffset,
    maxReportedHeight,
    minDelta,
    onHeightChange,
    rootRef,
  ])
}
